// src/components/lesson/diagrams/RectangularPrism.tsx
//
// Renders a labeled rectangular prism (box) using SVG oblique
// projection. Used by lessons and by the AI Tutor when a Volume
// question (V = ℓwh) needs a picture.
//
// Why oblique projection (not isometric):
//   Oblique keeps the FRONT face axis-aligned, so length and height
//   on the page match the math the student is doing. Isometric would
//   tilt all three axes, which is prettier but harder to label
//   clearly. For SAT-level Volume teaching, oblique is the right
//   readability tradeoff.
//
// Params shape:
//   {
//     "length": 5,         // ℓ — depth (front-to-back); proportional value
//     "width": 4,          // w — left-to-right
//     "height": 3,         // h — bottom-to-top
//     "lengthLabel": "ℓ",  // text rendered on the length edge (or "5", "5 cm")
//     "widthLabel": "w",
//     "heightLabel": "h",
//     "showHiddenEdges": true,  // dashed back edges, default true
//     "label": "Rectangular prism"  // accessible name
//   }
//
// Every param is optional; sensible defaults render a 4:3:2 box
// with no numeric labels (just ℓ/w/h text), which is the most
// generic "show me what a prism looks like" case.

type Params = {
  length?: number
  width?: number
  height?: number
  lengthLabel?: string
  widthLabel?: string
  heightLabel?: string
  showHiddenEdges?: boolean
  label?: string
}

// SVG canvas. Sized to match FunctionGraph so the chat layout is
// visually consistent across diagram types.
const W = 360
const H = 240
const PAD = 30

// Oblique projection: how much depth (length) tilts on the page.
// dx/dy at ~30° gives a recognizable 3D look without aggressive
// foreshortening. The 0.55 scale factor squishes depth so a "deep"
// box doesn't run off the canvas.
const ANGLE_DX = Math.cos((30 * Math.PI) / 180) * 0.55
const ANGLE_DY = -Math.sin((30 * Math.PI) / 180) * 0.55

export default function RectangularPrism({ params }: { params: Record<string, unknown> }) {
  const p = params as Params
  const length = typeof p.length === 'number' && p.length > 0 ? p.length : 4
  const width = typeof p.width === 'number' && p.width > 0 ? p.width : 4
  const height = typeof p.height === 'number' && p.height > 0 ? p.height : 3
  const showHidden = p.showHiddenEdges !== false

  // Normalize the longest axis to fit the canvas, then compute
  // per-axis pixel scale. Without normalization a "length: 100" box
  // would explode off the canvas while a "length: 0.1" would be
  // invisible. We treat the input numbers as PROPORTIONS, not pixel
  // sizes — the actual values are surfaced via *Label strings.
  const maxAxis = Math.max(length, width, height)
  const SCALE = Math.min(
    (W - 2 * PAD - length * 40 * ANGLE_DX) / width, // horizontal budget
    (H - 2 * PAD - length * 40 * Math.abs(ANGLE_DY)) / height, // vertical budget
  )
  // Pixel sizes after scaling.
  const w = Math.max(20, width * SCALE)
  const h = Math.max(20, height * SCALE)
  const d = Math.max(20, length * SCALE) // depth (along the oblique axis)
  void maxAxis // unused — kept for future "scale by absolute value" extensions

  // Bottom-left corner of the FRONT face, chosen so the whole
  // prism (front + the oblique depth offset) is centered.
  const depthOffsetX = d * ANGLE_DX
  const depthOffsetY = d * ANGLE_DY
  const frontLeft = (W - w - depthOffsetX) / 2
  const frontBottom = (H + h - depthOffsetY) / 2

  // The 8 vertices of the box, named by face (F=front, B=back) and
  // corner (BL, BR, TL, TR). Front-face corners use the simple
  // rectangle; back-face corners apply the depth offset.
  const FBL = { x: frontLeft, y: frontBottom }
  const FBR = { x: frontLeft + w, y: frontBottom }
  const FTL = { x: frontLeft, y: frontBottom - h }
  const FTR = { x: frontLeft + w, y: frontBottom - h }
  const BBL = { x: FBL.x + depthOffsetX, y: FBL.y + depthOffsetY }
  const BBR = { x: FBR.x + depthOffsetX, y: FBR.y + depthOffsetY }
  const BTL = { x: FTL.x + depthOffsetX, y: FTL.y + depthOffsetY }
  const BTR = { x: FTR.x + depthOffsetX, y: FTR.y + depthOffsetY }

  // Stroke for visible edges. Dashed stroke for hidden (back) edges
  // so they read as "behind" rather than overlapping the front.
  const stroke = 'rgb(34 211 238)' // cyan-400 — matches FunctionGraph
  const strokeW = 1.8

  // Labels are placed on a visible edge midpoint with a small
  // offset away from the box. Default text values are the
  // canonical math letters (ℓ, w, h) so an unlabeled diagram still
  // reads as "this is the length, this is the width, this is the
  // height."
  const lengthLabel = p.lengthLabel ?? 'ℓ'
  const widthLabel = p.widthLabel ?? 'w'
  const heightLabel = p.heightLabel ?? 'h'

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={p.label ?? 'Rectangular prism'}
      className="my-3 w-full max-w-md rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* Top face — drawn first so the front edges paint over it. */}
      <polygon
        points={`${FTL.x},${FTL.y} ${FTR.x},${FTR.y} ${BTR.x},${BTR.y} ${BTL.x},${BTL.y}`}
        fill="rgb(34 211 238 / 0.05)"
        stroke={stroke}
        strokeWidth={strokeW}
      />
      {/* Right face */}
      <polygon
        points={`${FTR.x},${FTR.y} ${BTR.x},${BTR.y} ${BBR.x},${BBR.y} ${FBR.x},${FBR.y}`}
        fill="rgb(34 211 238 / 0.05)"
        stroke={stroke}
        strokeWidth={strokeW}
      />
      {/* Front face — drawn last so its edges sit on top. */}
      <polygon
        points={`${FBL.x},${FBL.y} ${FBR.x},${FBR.y} ${FTR.x},${FTR.y} ${FTL.x},${FTL.y}`}
        fill="rgb(34 211 238 / 0.08)"
        stroke={stroke}
        strokeWidth={strokeW}
      />

      {/* Hidden edges — the three back edges that would be occluded
          in a solid model. Dashed so they don't compete with the
          visible silhouette. */}
      {showHidden && (
        <>
          {/* Back-bottom-left vertex's three hidden edges */}
          <line
            x1={BBL.x}
            y1={BBL.y}
            x2={BBR.x}
            y2={BBR.y}
            stroke={stroke}
            strokeWidth={strokeW * 0.7}
            strokeDasharray="4 3"
          />
          <line
            x1={BBL.x}
            y1={BBL.y}
            x2={BTL.x}
            y2={BTL.y}
            stroke={stroke}
            strokeWidth={strokeW * 0.7}
            strokeDasharray="4 3"
          />
          <line
            x1={BBL.x}
            y1={BBL.y}
            x2={FBL.x}
            y2={FBL.y}
            stroke={stroke}
            strokeWidth={strokeW * 0.7}
            strokeDasharray="4 3"
          />
        </>
      )}

      {/* Dimension labels.
            width  — bottom edge of front face
            height — left edge of front face
            length — top edge of depth (the oblique line at top) */}
      <text
        x={(FBL.x + FBR.x) / 2}
        y={FBL.y + 14}
        fontSize="12"
        fontWeight="600"
        fill="rgb(251 191 36)"
        textAnchor="middle"
      >
        {widthLabel}
      </text>
      <text
        x={FBL.x - 6}
        y={(FBL.y + FTL.y) / 2 + 4}
        fontSize="12"
        fontWeight="600"
        fill="rgb(251 191 36)"
        textAnchor="end"
      >
        {heightLabel}
      </text>
      <text
        x={(FTR.x + BTR.x) / 2 + 6}
        y={(FTR.y + BTR.y) / 2 - 4}
        fontSize="12"
        fontWeight="600"
        fill="rgb(251 191 36)"
      >
        {lengthLabel}
      </text>
    </svg>
  )
}
